import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ulid } from 'ulid';

export interface PricePreset {
  id: string;
  name: string;
  amount: number;
  currency: string;
  description?: string;
  tags: string[];
  category: 'transcription' | 'translation' | 'review' | 'pm' | 'custom';
  createdAt: Date;
  isActive: boolean;
}

interface PricePresetStore {
  presets: PricePreset[];
  isLoading: boolean;
  
  // Actions
  loadPresets: () => void;
  createPreset: (preset: Omit<PricePreset, 'id' | 'createdAt'>) => Promise<void>;
  updatePreset: (id: string, updates: Partial<PricePreset>) => Promise<void>;
  deletePreset: (id: string) => Promise<void>;
  getPresetsByCategory: (category: string) => PricePreset[];
  getPresetsByCurrency: (currency: string) => PricePreset[];
  searchPresets: (searchTerm: string) => PricePreset[];
}

// 샘플 데이터
const samplePresets: PricePreset[] = [
  {
    id: '01JGHPRN3BVUKFRP8FVTXSYQ4D',
    name: '전사 기본',
    amount: 15000,
    currency: 'KRW',
    description: '한국어 전사 기본 단가',
    tags: ['전사', '기본'],
    category: 'transcription',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRP5DXVKFRQ2GVTXSYQ5E',
    name: '전사 고급',
    amount: 25000,
    currency: 'KRW',
    description: '한국어 전사 고급 단가 (음질 불량)',
    tags: ['전사', '고급', '음질불량'],
    category: 'transcription',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRQ7FYWKFRN4HVTXSYQ6F',
    name: '번역 한→영',
    amount: 30000,
    currency: 'KRW',
    description: '한국어에서 영어로 번역',
    tags: ['번역', '한영'],
    category: 'translation',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRR9HZXKFRP6JVTXSYQ7G',
    name: '번역 영→한',
    amount: 25000,
    currency: 'KRW',
    description: '영어에서 한국어로 번역',
    tags: ['번역', '영한'],
    category: 'translation',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRSBK1YKFRQ8KVTXSYQ8H',
    name: '번역검수',
    amount: 20000,
    currency: 'KRW',
    description: '번역 검수 작업',
    tags: ['검수', '번역'],
    category: 'review',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRTDM3ZKFRN2LVTXSYQ9J',
    name: '전사검수',
    amount: 12000,
    currency: 'KRW',
    description: '전사 검수 작업',
    tags: ['검수', '전사'],
    category: 'review',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRUFP5AKFRP4MVTXSYQ1K',
    name: 'PM 확인',
    amount: 8000,
    currency: 'KRW',
    description: 'PM 최종 확인 작업',
    tags: ['PM', '확인'],
    category: 'pm',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRVHR7BKFRQ6NVTXSYQ2L',
    name: '일본어 번역',
    amount: 35000,
    currency: 'KRW',
    description: '한국어에서 일본어로 번역',
    tags: ['번역', '한일'],
    category: 'translation',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRWJT9CKFRN8PVTXSYQ3M',
    name: '중국어 번역',
    amount: 28000,
    currency: 'KRW',
    description: '한국어에서 중국어로 번역',
    tags: ['번역', '한중'],
    category: 'translation',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '01JGHPRXLV2DKFRP2QVTXSYQ4N',
    name: '긴급 작업',
    amount: 50000,
    currency: 'KRW',
    description: '긴급 작업 추가 단가',
    tags: ['긴급', '추가단가'],
    category: 'custom',
    createdAt: new Date('2024-01-01'),
    isActive: true,
  },
];

export const usePricePresetStore = create<PricePresetStore>()(
  persist(
    (set, get) => ({
      presets: [],
      isLoading: false,

      loadPresets: () => {
        const { presets } = get();
        if (presets.length === 0) {
          set({ presets: samplePresets });
        }
      },

      createPreset: async (presetData) => {
        const newPreset: PricePreset = {
          ...presetData,
          id: ulid(),
          createdAt: new Date(),
        };

        set((state) => ({
          presets: [...state.presets, newPreset],
        }));
      },

      updatePreset: async (id, updates) => {
        set((state) => ({
          presets: state.presets.map((preset) =>
            preset.id === id ? { ...preset, ...updates } : preset
          ),
        }));
      },

      deletePreset: async (id) => {
        set((state) => ({
          presets: state.presets.filter((preset) => preset.id !== id),
        }));
      },

      getPresetsByCategory: (category) => {
        const { presets } = get();
        return presets.filter(
          (preset) => preset.isActive && preset.category === category
        );
      },

      getPresetsByCurrency: (currency) => {
        const { presets } = get();
        return presets.filter(
          (preset) => preset.isActive && preset.currency === currency
        );
      },

      searchPresets: (searchTerm) => {
        const { presets } = get();
        if (!searchTerm.trim()) return presets.filter(p => p.isActive);
        
        const term = searchTerm.toLowerCase();
        return presets.filter(
          (preset) =>
            preset.isActive &&
            (preset.name.toLowerCase().includes(term) ||
              preset.description?.toLowerCase().includes(term) ||
              preset.tags.some(tag => tag.toLowerCase().includes(term)))
        );
      },
    }),
    {
      name: 'price-preset-store',
      version: 1,
    }
  )
);
